import {CognitoUserPool, CognitoUser, AuthenticationDetails} from 'amazon-cognito-identity-js';
import History from 'util/History';

const API_BASE = process.env.REACT_APP_API_BASE;
const getCognitoUser = (username) => {
  const poolData = {
    UserPoolId: process.env.REACT_APP_USER_POOL_ID,
    ClientId: process.env.REACT_APP_CLIENT_ID,
  };
  const userPool = new CognitoUserPool(poolData);
  const userData = {
    Username: username,
    Pool: userPool,
  };

  return new CognitoUser(userData);
};
const getAuthenticationDetails = (username, password) => {
  const authenticationData = {
    Username: username,
    Password: password,
  };

  return new AuthenticationDetails(authenticationData);
};

export const logout = () => {
  localStorage.removeItem('idToken');
  History.replace('/login');
};

export const getAuthorizationHeader = () => {
  const token = localStorage.getItem('idToken');

  return `Bearer ${token}`;
};

export const loggedIn = () => {
  const token = localStorage.getItem('idToken');

  return !!token;
};

export const cognitoAuthenticate = (username, password) => {
  const cognitoUser = getCognitoUser(username);
  const authenticationDetails = getAuthenticationDetails(username, password);

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess(result) {
        const idToken = result.getIdToken().getJwtToken();
        const email = result.getIdToken().payload.email || '';

        resolve({idToken, email});
      },
      onFailure(err) {
        if (err.code === 'PasswordResetRequiredException') {
          cognitoUser.forgotPassword({
            inputVerificationCode() {
              History.replace('/reset-password');
            },
          });
        } else {
          reject(err);
        }
      },
      newPasswordRequired() {
        localStorage.setItem('temporaryUsername', username);
        localStorage.setItem('temporaryPassword', password);
        History.replace('/new-password');
      },
    });
  });
};

export const completeLogin = (idToken) => {
  localStorage.setItem('idToken', idToken);
  History.replace('/');
};

export const requestMfaCode = async (idToken) => {
  const response = await fetch(`${API_BASE}/api/v1/mfa/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({message: 'Failed to send verification code'}));

    throw new Error(error.message || 'Failed to send verification code');
  }

  return response.json();
};

export const verifyMfaCode = async (idToken, code) => {
  const response = await fetch(`${API_BASE}/api/v1/mfa/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({code}),
  });
  const data = await response.json();

  if (!response.ok || !data.verified) {
    throw new Error(data.message || 'Invalid or expired verification code');
  }

  return data;
};

export const cognitoLogin = (username, password) => {
  const cognitoUser = getCognitoUser(username);
  const authenticationDetails = getAuthenticationDetails(username, password);

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess(result) {
        const idToken = result.getIdToken().getJwtToken();

        localStorage.setItem('idToken', idToken);
        History.replace('/');
      },
      onFailure(err) {
        if (err.code === 'PasswordResetRequiredException') {
          cognitoUser.forgotPassword({
            inputVerificationCode() {
              History.replace('/reset-password');
            },
          });
        } else {
          reject(err);
        }
      },
      newPasswordRequired() {
        localStorage.setItem('temporaryUsername', username);
        localStorage.setItem('temporaryPassword', password);
        History.replace('/new-password');
      },
    });
  });
};

export const resetPasswordCognito = (newPassword, verificationCode) => {
  const username = localStorage.getItem('temporaryUsername');
  const cognitoUser = getCognitoUser(username);

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess() {
        History.replace('/login');
        resolve();
      },
      onFailure(err) {
        if (err.code === 'MinRangeError') {
          reject(new Error('Password too short'));
        } else {
          reject(err);
        }
      },
    });
  });
};

export const sendForgotPasswordEmail = (username) => {
  const cognitoUser = getCognitoUser(username);

  localStorage.setItem('temporaryUsername', username);

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess() {
        History.replace('/login');
        resolve();
      },
      onFailure(err) {
        reject(err);
      },
      inputVerificationCode() {
        History.replace('/reset-password');
      },
    });
  });
};

export const firstTimeLoginResetPassword = (newPassword) => {
  const temporaryUsername = localStorage.getItem('temporaryUsername');
  const temporaryPassword = localStorage.getItem('temporaryPassword');
  const cognitoUser = getCognitoUser(temporaryUsername);
  const authenticationDetails = getAuthenticationDetails(temporaryUsername, temporaryPassword);

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onFailure(err) {
        reject(err);
      },
      newPasswordRequired(userAttributes) {
        // eslint-disable-next-line camelcase
        const {email_verified, ...userAtts} = userAttributes;

        cognitoUser.completeNewPasswordChallenge(newPassword, userAtts, {
          onSuccess() {
            History.replace('/');
          },
          onFailure(err) {
            reject(err);
          },
        });
      },
    });
  });
};
